import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Alert, Button, IconButton, Paper, Stack, Typography } from "@mui/material";
import DeleteIcon from "assets/icons/DeleteIcon";
import { ChangeEvent, useRef } from "react";
import { useController, useFormContext } from "react-hook-form";
import { DOCUMENT_ACCEPT, MAX_GENERATION_DOCUMENTS } from "../constants";
import { CompetencyMapGenerationFormValues } from "../types";
import { formatFileSize } from "../utils";

const DocumentsUpload = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { setError, clearErrors, watch } = useFormContext<CompetencyMapGenerationFormValues>();
  const {
    field,
    fieldState: { error },
  } = useController<CompetencyMapGenerationFormValues>({
    name: "files",
    rules: {
      validate: (value) => ((value as File[])?.length ? true : "Добавьте хотя бы один документ"),
    },
  });

  const files = watch("files") || [];
  const hasPdfFiles = files.some((file) => file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf"));

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { target } = event;
    const uploadedFiles = target.files ? Array.from(target.files) : [];
    const nextFiles = [...files, ...uploadedFiles].filter(
      (file, index, fileList) =>
        fileList.findIndex(
          (currentFile) => currentFile.name === file.name && currentFile.lastModified === file.lastModified
        ) === index
    );

    if (nextFiles.length > MAX_GENERATION_DOCUMENTS) {
      setError("files", {
        type: "manual",
        message: `Можно загрузить не более ${MAX_GENERATION_DOCUMENTS} файлов`,
      });
      target.value = "";
      return;
    }

    clearErrors("files");
    field.onChange(nextFiles);
    target.value = "";
  };

  const handleRemoveFile = (index: number) => {
    const nextFiles = files.filter((_, fileIndex) => fileIndex !== index);
    field.onChange(nextFiles);
    if (!nextFiles.length && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Stack
      spacing={1.5}
      justifyContent='space-between'
      sx={{ height: "100%" }}>
      <Stack spacing={1}>
        {files.map((file, index) => (
          <Paper
            key={`${file.name}-${file.lastModified}`}
            variant='outlined'
            sx={{
              p: 1,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
            }}>
            <Stack
              spacing={0.25}
              sx={{ minWidth: 0 }}>
              <Typography
                fontSize={14}
                fontWeight={600}
                sx={{ wordBreak: "break-word" }}>
                {file.name}
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'>
                {formatFileSize(file.size)}
              </Typography>
            </Stack>
            <IconButton
              color='primary'
              onClick={() => handleRemoveFile(index)}>
              <DeleteIcon />
            </IconButton>
          </Paper>
        ))}
      </Stack>
      <Stack spacing={1.5}>
        {files.length < MAX_GENERATION_DOCUMENTS && (
          <Button
            variant='contained'
            startIcon={<UploadFileIcon />}
            onClick={handleOpenFilePicker}
            sx={{ alignSelf: "flex-start" }}>
            Загрузить документ
          </Button>
        )}
        <input
          ref={fileInputRef}
          type='file'
          hidden
          multiple
          accept={DOCUMENT_ACCEPT}
          onChange={handleChange}
        />
        <Typography
          variant='body2'
          color='text.secondary'>
          txt, md, docx, json, pdf • до {MAX_GENERATION_DOCUMENTS} файлов
        </Typography>
        {error?.message && (
          <Typography
            variant='body2'
            color='error.main'>
            {error.message}
          </Typography>
        )}
        {hasPdfFiles && (
          <Alert
            severity='info'
            sx={{ py: 1.5 }}>
            PDF может содержать изображения, проверьте результат после генерации.
          </Alert>
        )}
      </Stack>
    </Stack>
  );
};

export default DocumentsUpload;
